"use client";

import { motion } from "motion/react";
import { TeamMember } from "@/constants/about-us";
import { EASE } from "@/lib/motion";

type Props = {
  member: TeamMember;
};

type SectionHeadProps = {
  volume: string;
  title: string;
  accent: string;
  caption: string;
};

function SectionHead({ volume, title, accent, caption }: SectionHeadProps) {
  return (
    <motion.aside
      variants={{
        hidden: { opacity: 0, y: 14 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.7, ease: EASE },
        },
      }}
      className="col-span-12 lg:col-span-3 mb-8 lg:mb-0"
    >
      <span className="font-serif italic text-brand-800 text-[15px] tracking-[-0.005em]">
        {volume}
      </span>
      <h2 className="mt-3 text-[clamp(1.5rem,2.4vw,2rem)] leading-[1.04] tracking-[-0.03em] text-foreground">
        {title}
        <span className="font-serif italic font-normal text-brand-800">
          {accent}
        </span>
      </h2>
      <span aria-hidden className="block h-px w-10 mt-5 bg-brand-800/40" />
      <p className="mt-5 text-[10.5px] uppercase tracking-[0.22em] text-foreground/45">
        {caption}
      </p>
    </motion.aside>
  );
}

type CredentialListProps = {
  heading: string;
  items: string[];
};

function CredentialList({ heading, items }: CredentialListProps) {
  return (
    <motion.section
      variants={{
        hidden: { opacity: 0, y: 18 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.85, ease: EASE },
        },
      }}
      className="flex flex-col"
    >
      <header className="flex items-baseline justify-between pb-3 border-b border-foreground/15">
        <h3 className="font-serif italic text-foreground text-base tracking-[-0.005em]">
          {heading}
        </h3>
        <span className="text-[10.5px] uppercase tracking-[0.22em] text-foreground/45 tabular-nums">
          {String(items.length).padStart(2, "0")} stavki
        </span>
      </header>

      <ol className="flex flex-col">
        {items.map((item, idx) => (
          <li
            key={item}
            className="grid grid-cols-[2.75rem_1fr] gap-4 py-4 border-b border-foreground/10 last:border-b-0"
          >
            <span className="font-serif italic text-brand-800/65 text-[15px] tabular-nums pt-px">
              {String(idx + 1).padStart(2, "0")}
            </span>
            <span className="text-foreground/80 leading-[1.55] text-[15px] tracking-[-0.005em]">
              {item}
            </span>
          </li>
        ))}
      </ol>
    </motion.section>
  );
}

export function MemberDesc({ member }: Props) {
  const hasCredentials =
    (member.degrees && member.degrees.length > 0) ||
    (member.training && member.training.length > 0);

  return (
    <section className="px-4 md:px-8 lg:px-12 pt-10">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
        variants={{
          hidden: {},
          visible: { transition: { staggerChildren: 0.08 } },
        }}
        className="grid grid-cols-12 gap-x-6 lg:gap-x-10 border-t border-foreground/10 pt-12 lg:pt-16"
      >
        <SectionHead
          volume="Vol. II"
          title="Bio"
          accent="grafija"
          caption="Put i posvećenost"
        />

        <motion.div
          variants={{
            hidden: { opacity: 0, y: 18 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.85, ease: EASE },
            },
          }}
          className="col-span-12 lg:col-span-9"
        >
          <p
            className="text-foreground/80 leading-[1.78] tracking-[-0.005em] text-[15.5px] md:text-[16px] max-w-[68ch]
              first-letter:font-serif first-letter:italic first-letter:font-normal
              first-letter:text-brand-800 first-letter:text-[5.25em] first-letter:leading-[0.82]
              first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:-ml-1"
          >
            {member.description}
          </p>
        </motion.div>
      </motion.div>

      {hasCredentials && (
        <div className="mt-20 lg:mt-28">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.08 } },
            }}
            className="grid grid-cols-12 gap-x-6 lg:gap-x-10 border-t border-foreground/10 pt-12 lg:pt-16"
          >
            <SectionHead
              volume="Vol. III"
              title="Akademski "
              accent="put"
              caption="Obrazovanje & edukacije"
            />

            <div className="col-span-12 lg:col-span-9 flex flex-col gap-12 lg:gap-14">
              {member.degrees && member.degrees.length > 0 && (
                <CredentialList heading="Obrazovanje" items={member.degrees} />
              )}

              {member.training && member.training.length > 0 && (
                <CredentialList
                  heading="Dodatne edukacije"
                  items={member.training}
                />
              )}
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
